import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Download,
  FlaskConical,
  Loader2,
  RotateCcw,
  Save,
} from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import OwnerLayout from "@/components/OwnerLayout";
import ImagePicker from "@/components/ImagePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Update one field inside one section without disturbing the rest. */
function setIn(current, section, key, value) {
  const base = current || {};
  return { ...base, [section]: { ...(base[section] || {}), [key]: value } };
}

function Section({ title, hint, children }) {
  return (
    <div className="panel panel-pad">
      <div className="eyebrow">{title}</div>
      {hint && <p className="text-[12.5px] text-muted2 mt-1.5 max-w-lg">{hint}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

export default function Platform() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [pub, setPub] = useState(null);
  const [brand, setBrand] = useState({ logo: "", favicon: "" });
  const [snapKeep, setSnapKeep] = useState("5");
  const [snapDays, setSnapDays] = useState("30");
  const [backup, setBackup] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, ov, b] = await Promise.all([
        api.get("/platform"),
        api.get("/platform/overview"),
        api.get("/admin/backup/status"),
      ]);
      setName(p.data.platform_name || "");
      setPub(p.data.public || null);
      setBrand({ logo: p.data.logo || "", favicon: p.data.favicon || "" });
      setSnapKeep(String(p.data.snapshot_keep ?? 5));
      setSnapDays(String(p.data.snapshot_days ?? 30));
      setWorkspace(ov.data.workspace || null);
      setBackup(b.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveBranding = async (next) => {
    setBrand(next);
    try {
      await api.put("/platform/branding", next);
      toast.success("Saved — the tab icon updates on the next page load");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const takeBackup = async () => {
    setDownloading(true);
    try {
      // Streams a JSON file of every client. Keep it somewhere off this server.
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `platform-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — store it somewhere safe");
      const b = await api.get("/admin/backup/status");
      setBackup(b.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDownloading(false);
    }
  };

  const setReminder = async (value) => {
    try {
      await api.put("/platform", { backup_reminder: value });
      const b = await api.get("/admin/backup/status");
      setBackup(b.data);
      toast.success(value === "off" ? "Reminder switched off" : `Reminder set to ${value}`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resetWorkspace = async () => {
    const ok = await confirm({
      title: "Clear your workspace?",
      body: "Every conversation and message in your sandbox goes. No real client is affected.",
      confirmText: "Clear it",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete("/platform/workspace");
      toast.success("Workspace reset to empty");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/platform", {
        platform_name: name,
        public: pub || undefined,
        snapshot_keep: Number(snapKeep),
        snapshot_days: Number(snapDays),
      });
      toast.success("Platform settings saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <OwnerLayout
      title="Platform settings"
      subtitle="Yours, not any client's"
      action={
        <Button onClick={save} disabled={busy || loading} className="bg-brand hover:bg-brand-hover">
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Save</span>
            </>
          )}
        </Button>
      }
    >
      <Tabs defaultValue="platform" className="max-w-5xl">
        <TabsList className="bg-brand-light/60 mb-5">
          <TabsTrigger value="platform">Platform</TabsTrigger>
          <TabsTrigger value="pages">Public pages</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
        </TabsList>

        <TabsContent value="platform">
          <div className="grid lg:grid-cols-2 gap-5">
        <Section
          title="Backup"
          hint="Downloads every client's conversations, messages and settings as one file. Keep it somewhere other than this server."
        >
          <div className="space-y-3">
            {backup?.overdue && (
              <div className="rounded-xl border border-amber-200 bg-gold-light px-4 py-3 flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                <p className="text-[12.5px] text-gold leading-relaxed">
                  {backup.last_backup_at
                    ? `Last backup was ${backup.days_since} days ago.`
                    : "You haven't taken a backup yet."}{" "}
                  Atlas's free tier keeps no copies, so this file is your only way back.
                </p>
              </div>
            )}

            <Button
              onClick={takeBackup}
              disabled={downloading}
              className="w-full bg-brand hover:bg-brand-hover"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" /> Download a backup now
                </>
              )}
            </Button>

            <div className="flex items-center justify-between text-[12.5px]">
              <span className="text-muted2">Last taken</span>
              <span className="text-ink">
                {backup?.last_backup_at ? formatDate(backup.last_backup_at) : "Never"}
              </span>
            </div>

            <div className="space-y-1.5 pt-1">
              <Label>Remind me</Label>
              <Select value={backup?.reminder || "weekly"} onValueChange={setReminder}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Never remind me</SelectItem>
                  <SelectItem value="daily">Every day</SelectItem>
                  <SelectItem value="weekly">Every week</SelectItem>
                  <SelectItem value="fortnightly">Every two weeks</SelectItem>
                  <SelectItem value="monthly">Every month</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[12px] text-muted2">
                This is a reminder, not an automatic backup — the server sleeps when
                nobody's using it, so nothing can run on a timer. Each client's admin
                can also download their own from their Settings page.
              </p>
            </div>
          </div>
        </Section>

        <Section
          title="Safety copies"
          hint="Before anything destructive — like deleting a client account — the app saves a copy that can be restored."
        >
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="sk">Keep per client</Label>
                <Input
                  id="sk"
                  type="number"
                  min="0"
                  max="20"
                  value={snapKeep}
                  onChange={(e) => setSnapKeep(e.target.value)}
                />
                <p className="text-[12px] text-muted2">
                  Oldest dropped first. 0 switches this off.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sd">Keep for</Label>
                <Input
                  id="sd"
                  type="number"
                  min="0"
                  max="365"
                  value={snapDays}
                  onChange={(e) => setSnapDays(e.target.value)}
                />
                <p className="text-[12px] text-muted2">
                  Days. 0 keeps them until the limit above pushes them out.
                </p>
              </div>
            </div>
            <p className="text-[12px] text-muted2 leading-relaxed">
              Copies live in the database, so more of them means a larger database.
              Five per client is usually plenty — they exist for the mistake you
              notice within the hour, not as a long-term archive. For that, download
              a backup.
            </p>
          </div>
        </Section>

        <Section
          title="Branding"
          hint="Shown on the sign-in screen, before anyone has logged in. Clients set their own business name separately."
        >
          <div className="space-y-1.5">
            <Label htmlFor="pname">Platform name</Label>
            <Input
              id="pname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Team Chat"
            />
            <p className="text-[12px] text-muted2">
              Everyone sees this when they sign in, so keep it neutral rather than
              naming one client.
            </p>
          </div>
        </Section>

        <Section title="Your account" hint="Change your name or password from your profile.">
          <div className="space-y-3">
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-muted2">Name</span>
              <span className="text-ink font-medium">{user?.name}</span>
            </div>
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-muted2">Email</span>
              <span className="text-ink">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-muted2">Role</span>
              <span className="text-ink font-medium">Platform owner</span>
            </div>
            <Button asChild variant="outline" className="mt-2">
              <a href="/profile">Edit profile</a>
            </Button>
          </div>
        </Section>

        <Section
          title="Your workspace"
          hint="Your own sandbox for trying things out. It's a normal client workspace in every way except that it's never counted as a client or shown in your client list."
        >
          {workspace ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-xl border border-line px-3.5 py-3">
                <FlaskConical className="w-4 h-4 text-gold shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-medium text-ink">{workspace.name}</div>
                </div>
              </div>
              <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4">
                <div className="text-[13.5px] font-medium text-ink">Reset the workspace</div>
                <p className="text-[12.5px] text-muted2 mt-1">
                  Clears every conversation and message in your sandbox. No real client is affected.
                </p>
                <Button
                  variant="outline"
                  onClick={resetWorkspace}
                  className="mt-3 border-rose-200 text-stock-out hover:bg-rose-100 hover:text-stock-out"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset to empty
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-[13px] text-muted2">
              Not created yet. Open it from the Overview page and it'll be set up
              automatically.
            </p>
          )}
        </Section>

        <Section
          title="How clients are kept apart"
          hint="Worth knowing if a client ever asks."
        >
          <div className="space-y-2.5 text-[13px] text-muted2 leading-relaxed">
            <p>
              Every conversation and message is tagged with the client
              account it belongs to, and every query filters on that tag.
            </p>
            <p>
              Client admins and agents are locked to their own business on the server,
              so they can't reach another client's data even by tampering with the
              request. Only your account can move between them.
            </p>
            <p>
              Deleting a client removes their entire history permanently, which is why
              it asks you to type the name back.
            </p>
          </div>
        </Section>
      </div>
        </TabsContent>

        <TabsContent value="branding">
          <div className="grid lg:grid-cols-2 gap-5">
            <Section
              title="Logo"
              hint="Shown in the sidebar, on the sign-in screen and on the landing page. Without one, the first letter of the platform name is used."
            >
              <ImagePicker
                label="Platform logo"
                hint="A square image works best. PNG or SVG, under 400KB."
                value={brand.logo}
                onChange={(logo) => saveBranding({ ...brand, logo })}
              />
            </Section>

            <Section
              title="Tab icon"
              hint="The small icon in the browser tab and on a bookmark."
            >
              <ImagePicker
                label="Favicon"
                hint="32×32 or larger, square. PNG or ICO."
                value={brand.favicon}
                onChange={(favicon) => saveBranding({ ...brand, favicon })}
              />
              <p className="text-[12px] text-muted2 mt-3 leading-relaxed">
                Browsers hold on to tab icons stubbornly — you may need a hard
                refresh, or to close and reopen the tab, before a change shows.
              </p>
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="pages">
          <div className="grid lg:grid-cols-2 gap-5">
            <Section
              title="Sign-in screen"
              hint="What people read on the way in. Leave a field empty to use the wording that ships with the app."
            >
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="l-tagline">Headline</Label>
                  <Input
                    id="l-tagline"
                    value={pub?.login?.tagline || ""}
                    onChange={(e) => setPub(setIn(pub, "login", "tagline", e.target.value))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="l-body">Supporting text</Label>
                  <textarea
                    id="l-body"
                    rows={4}
                    value={pub?.login?.body || ""}
                    onChange={(e) => setPub(setIn(pub, "login", "body", e.target.value))}
                    className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="l-heading">Form heading</Label>
                    <Input
                      id="l-heading"
                      value={pub?.login?.heading || ""}
                      onChange={(e) => setPub(setIn(pub, "login", "heading", e.target.value))}
                      placeholder="Sign in"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="l-sub">Under the heading</Label>
                    <Input
                      id="l-sub"
                      value={pub?.login?.sub || ""}
                      onChange={(e) => setPub(setIn(pub, "login", "sub", e.target.value))}
                    />
                  </div>
                </div>
              </div>
            </Section>

            <Section
              title="Landing page"
              hint="The page people see at your address before signing in. Switched off, they go straight to the sign-in screen."
            >
              <div className="space-y-4">
                <div className="rounded-xl border border-line p-3.5 flex items-start gap-3">
                  <Switch
                    checked={!!pub?.landing?.enabled}
                    onCheckedChange={(v) => setPub(setIn(pub, "landing", "enabled", v))}
                    className="mt-0.5 shrink-0"
                  />
                  <div>
                    <div className="text-[13.5px] font-medium text-ink">Show a landing page</div>
                    <p className="text-[12px] text-muted2 mt-0.5">
                      Off by default — clients who only ever sign in don't need one.
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="h-head">Headline</Label>
                  <Input
                    id="h-head"
                    value={pub?.landing?.headline || ""}
                    onChange={(e) => setPub(setIn(pub, "landing", "headline", e.target.value))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="h-sub">Supporting text</Label>
                  <textarea
                    id="h-sub"
                    rows={3}
                    value={pub?.landing?.subhead || ""}
                    onChange={(e) => setPub(setIn(pub, "landing", "subhead", e.target.value))}
                    className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="h-points">Points, one per line</Label>
                  <textarea
                    id="h-points"
                    rows={4}
                    value={(pub?.landing?.points || []).join("\n")}
                    onChange={(e) =>
                      setPub(setIn(pub, "landing", "points", e.target.value.split("\n")))
                    }
                    className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                  />
                  <p className="text-[12px] text-muted2">Blank lines are ignored.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="h-cta">Button label</Label>
                    <Input
                      id="h-cta"
                      value={pub?.landing?.cta || ""}
                      onChange={(e) => setPub(setIn(pub, "landing", "cta", e.target.value))}
                      placeholder="Sign in"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="h-foot">Footer</Label>
                    <Input
                      id="h-foot"
                      value={pub?.landing?.footer || ""}
                      onChange={(e) => setPub(setIn(pub, "landing", "footer", e.target.value))}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <a
                  href="/"
                  target="_blank"
                  rel="noreferrer"
                  className="link-quiet text-[13px] font-medium inline-block"
                >
                  Open the landing page
                </a>
              </div>
            </Section>
          </div>
        </TabsContent>
      </Tabs>
    </OwnerLayout>
  );
}
